import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IconButton, Modal, useTheme } from '@mui/material';
import { EditApiKeyForm } from 'components/Drawer/types';
import DeleteApiKey from 'components/Modal/variants/DeleteApiKey';
import useDrawer from 'hooks/useDrawer/useDrawer';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useRestApiKey from 'hooks/useRestApiKey/useRestApiKey';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { EditApiKeyDrawer } from 'state/types';
import DrawerTemplate from 'ui/organisms/Drawer/DrawerTemplate';
import EditApiKeyUI from 'ui/organisms/Drawer/variants/EditApiKey';
import { API_KEY_MAX_NAME_LENGTH } from 'v2/constants';

const EditApiKey: React.FC<React.PropsWithChildren> = () => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const { register, handleSubmit } = useForm<EditApiKeyForm>();
  const { drawerState } = useDrawer<EditApiKeyDrawer>();
  const { hideDrawer } = useDrawer();
  const [nameInputValue, setNameInputValue] = useState<string>();
  const [nameInputValidation, setNameInputValidation] = useState<string | undefined>(undefined);
  const [referenceInputValue, setReferenceInputValue] = useState<string>();
  const [creationDateInputValue, setCreationDateInputValue] = useState<string>();
  const [showValidation, setShowValidation] = useState(false);
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { updateRestApiKey, updateRestApiKeyLoading } = useRestApiKey({ handleFetchError });
  const [isDeleteKeyModalOpen, setIsDeleteKeyModalOpen] = useState(false);

  const handleOpenDeleteKeyModal = () => setIsDeleteKeyModalOpen(true);
  const handleCloseDeleteKeyModal = () => setIsDeleteKeyModalOpen(false);

  const onSubmit = async () => {
    setShowValidation(true);
    if (!updateRestApiKeyLoading && nameInputValidation === undefined) {
      try {
        await updateRestApiKey(drawerState.contentValue.id, nameInputValue || '');
      } finally {
        hideDrawer();
      }
    }
  };

  const handleNameInputOnChange = (value: string) => {
    setNameInputValue(value);
    setNameInputValidation(undefined);
    if (value.length > API_KEY_MAX_NAME_LENGTH) setNameInputValidation(translation.group_name_too_long_error);
  };

  useEffect(() => {
    if (drawerState?.contentValue) {
      if (nameInputValue === undefined) setNameInputValue(drawerState.contentValue.name);
      if (referenceInputValue === undefined) setReferenceInputValue(drawerState.contentValue.reference);
      if (creationDateInputValue === undefined) setCreationDateInputValue(drawerState.contentValue.creationDate);
    }
  }, [creationDateInputValue, drawerState, nameInputValue, referenceInputValue]);

  return (
    <DrawerTemplate
      title={translation.edit_api_key}
      confirmButtonText={translation.save}
      closeButtonText={translation.cancel}
      closeButton
      headerSideText={
        <IconButton
          size="small"
          style={{ color: theme.palette.error.main }}
          id="drawer-delete-api-key"
          onClick={handleOpenDeleteKeyModal}
        >
          <DeleteOutlineIcon fontSize="medium" data-testid="svg-delete" style={{ marginLeft: 0 }} />
        </IconButton>
      }
      id="drawer-edit-api-key"
      onSubmit={handleSubmit(onSubmit)}
      isLoading={updateRestApiKeyLoading}
      disableConfirmButton={showValidation && nameInputValidation !== undefined && updateRestApiKeyLoading}
    >
      <EditApiKeyUI
        register={register}
        nameInputValue={nameInputValue || ''}
        handleNameInputOnChange={handleNameInputOnChange}
        referenceInputValue={referenceInputValue}
        showValidation={showValidation}
        nameInputValidation={nameInputValidation}
        creationDateInputValue={creationDateInputValue}
      />
      <Modal
        open={isDeleteKeyModalOpen}
        onClose={handleCloseDeleteKeyModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <DeleteApiKey
          handleClose={handleCloseDeleteKeyModal}
          id={drawerState.contentValue.id}
          apiKey={drawerState.contentValue.reference}
          description={drawerState.contentValue.name}
        />
      </Modal>
    </DrawerTemplate>
  );
};
export default EditApiKey;
